import { Component, computed, HostListener, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PostCode } from '../../../global/postcode';
import { AnimateOpenDirective } from '../../directives/animate-open.directive';
import { ButtonComponent } from '../button/button.component';
import { TextInputComponent } from '../forms/text-input/text-input.component';

@Component({
  selector: 'app-address-lookup',
  imports: [ButtonComponent, TextInputComponent, FormsModule, AnimateOpenDirective],
  templateUrl: './address-lookup.component.html',
  styleUrl: './address-lookup.component.scss',
})
export class AddressLookupComponent {
  @HostListener('window:mousedown', ['$event'])
  handleMouseEvent(event: MouseEvent) {
    if (!(event.target as HTMLElement)?.closest('.auto-search')) {
      this.showAddressList = false;
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (!['ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }

    const autoSearch = document.querySelector('.auto-search');
    if (!autoSearch || !autoSearch.contains(document.activeElement)) {
      return;
    }

    event.preventDefault();

    const focusableElements = Array.from(autoSearch.querySelectorAll('input, button:not([disabled])')) as HTMLElement[];

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

    let nextIndex = 0;
    if (event.key === 'ArrowDown') {
      nextIndex = currentIndex + 1 < focusableElements.length ? currentIndex + 1 : 0;
    } else if (event.key === 'ArrowUp') {
      nextIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : focusableElements.length - 1;
    }

    focusableElements[nextIndex]?.focus();
  }

  autoSearchAddress = '';
  addressFound: any[] | undefined;
  addressesLoaded = false;
  loadingAddresses = false;

  selectedTopLevel: any;
  selectedSecondLevel: any;
  showAddressList = false;
  addressValue = {
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    postalCode: '',
  };

  addressFormError = false;
  addressSaved = output<typeof this.addressValue>();

  addressWasSaved = signal(false);
  isAutoFilling = false;

  addressPanelExpanded = signal(false);

  private viewModel = computed(() => {
    return {
      addressPanelExpanded: this.addressPanelExpanded(),
    };
  });

  get vm() {
    return this.viewModel();
  }

  lookupAddress(searchTerm: string, id?: string, autoFocus = false): void {
    this.loadingAddresses = true;
    if (searchTerm === '') return;
    PostCode.lookup(searchTerm, id).subscribe({
      next: (value) => {
        this.addressFound = value;
        this.loadingAddresses = false;
        this.addressesLoaded = true;
        this.showAddressList = true;

        if (autoFocus) {
          setTimeout(() => {
            (document.querySelector('.address-list button') as HTMLElement)?.focus();
          });
        }
      },
    });
  }

  saveAddress(): void {
    if (!this.addressValue!['addressLine1'] || !this.addressValue!['postalCode']) {
      this.addressFormError = true;
      return;
    }

    this.addressSaved.emit(this.addressValue);
    this.addressWasSaved.set(true);
  }

  selectAddress(address: { Description: string; Id: string; Text: string }) {
    const splitDescription = address.Description.split(', ');
    const postalCode = splitDescription.pop() || '';
    const [addressLine2 = '', addressLine3 = ''] =
      splitDescription.length === 1 ? ['', ...splitDescription] : splitDescription;

    this.isAutoFilling = true;

    this.addressValue = {
      addressLine1: address.Text,
      addressLine2,
      addressLine3,
      postalCode,
    };

    this.addressFormError = false;
    this.showAddressList = false;
    this.selectedSecondLevel = address;

    this.addressPanelExpanded.set(true);

    setTimeout(() => {
      this.addressWasSaved.set(true);
      this.isAutoFilling = false;

      setTimeout(() => {
        this.addressWasSaved.set(false);
      }, 2000);
    });
  }

  selectTopLevelAddress(address: { Description: string; Id: string; Text: string }) {
    this.selectedTopLevel = address;
    this.selectedSecondLevel = undefined;
    this.lookupAddress(this.autoSearchAddress, address.Id, true);
  }

  onAddressFieldChange(): void {
    if (!this.isAutoFilling) {
      this.addressWasSaved.set(false);
    }
  }
}
